import type { Location } from 'react-router';

// ----------------------------------------------------------------------

export const MODAL_SEARCH_PARAM = 'modal';

const MODAL_ROUTE_PATTERNS = [
  /^\/folder\/\d+\/equipment\/new$/,
  /^\/folder\/\d+\/equipment\/\d+$/,
  /^\/folder\/\d+\/equipment\/\d+\/edit$/,
  /^\/equipment\/new$/,
  /^\/equipment\/\d+$/,
  /^\/equipment\/\d+\/edit$/,
  /^\/users\/new$/,
  /^\/users\/\d+$/,
  /^\/users\/\d+\/edit$/,
  /^\/training\/new$/,
  /^\/training\/\d+$/,
  /^\/training\/\d+\/edit$/,
  /^\/checklist\/new$/,
  /^\/checklist\/\d+$/,
  /^\/checklist\/\d+\/edit$/,
  /^\/inspection\/\d+$/,
  /^\/folder\/new$/,
  /^\/folder\/\d+$/,
  /^\/folder\/\d+\/edit$/,
  /^\/tags\/new$/,
  /^\/tags\/\d+\/edit$/,
  /^\/models\/new$/,
  /^\/models\/\d+\/edit$/,
  /^\/repairs\/new$/,
  /^\/repairs\/\d+$/,
  /^\/repairs\/\d+\/review$/,
  /^\/repairs\/\d+\/reinspect$/,
  /^\/transfers\/\d+$/,
];

const getOrigin = () =>
  typeof window === 'undefined' ? 'http://localhost' : window.location.origin;

const getPathname = (href: string): string | null => {
  try {
    const url = new URL(href, getOrigin());
    if (url.origin !== getOrigin()) {
      return null;
    }
    return url.pathname;
  } catch {
    return null;
  }
};

export function isModalRoutePath(href: string | undefined): boolean {
  if (!href) {
    return false;
  }

  const pathname = getPathname(href);
  return (
    !!pathname && MODAL_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname))
  );
}

/** True when a same-origin `href` targets `path` or one of its sub-paths. */
export function hrefTargetsPath(href: string, path: string): boolean {
  const pathname = getPathname(href);
  return !!pathname && (pathname === path || pathname.startsWith(`${path}/`));
}

export function getModalPathFromLocation(
  location: Pick<Location, 'search'>,
): string | null {
  const modalPath = new URLSearchParams(location.search).get(
    MODAL_SEARCH_PARAM,
  );
  if (!modalPath || !isModalRoutePath(modalPath)) {
    return null;
  }

  return modalPath;
}

export function hasModalParam(
  location: Pick<Location, 'search'> = currentBrowserLocation(),
): boolean {
  return new URLSearchParams(location.search).has(MODAL_SEARCH_PARAM);
}

function currentBrowserLocation(): Pick<
  Location,
  'pathname' | 'search' | 'hash'
> {
  if (typeof window === 'undefined') {
    return {
      pathname: '/',
      search: '',
      hash: '',
    };
  }

  return {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  };
}

function removeModalParam(search: string): string {
  const searchParams = new URLSearchParams(search);
  searchParams.delete(MODAL_SEARCH_PARAM);
  const nextSearch = searchParams.toString();
  return nextSearch ? `?${nextSearch}` : '';
}

export function getBackgroundHref(
  location: Pick<
    Location,
    'pathname' | 'search' | 'hash'
  > = currentBrowserLocation(),
): string {
  return `${location.pathname}${removeModalParam(location.search)}${location.hash}`;
}

export function buildModalHref(
  href: string,
  location: Pick<
    Location,
    'pathname' | 'search' | 'hash'
  > = currentBrowserLocation(),
): string {
  if (!isModalRoutePath(href)) {
    return href;
  }

  const backgroundSearch = new URLSearchParams(
    removeModalParam(location.search),
  );
  backgroundSearch.set(MODAL_SEARCH_PARAM, href);
  const nextSearch = backgroundSearch.toString();

  return `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${location.hash}`;
}

/**
 * Resolve a modal navigation from either the real page location or the
 * synthetic location used to render an open modal.
 */
export function resolveModalHref(
  href: string,
  location: Pick<Location, 'pathname' | 'search' | 'hash' | 'key'>,
  browserLocation: Pick<
    Location,
    'pathname' | 'search' | 'hash'
  > = currentBrowserLocation(),
): string {
  return location.key === 'modal'
    ? buildModalHref(href, browserLocation)
    : buildModalHref(href, location);
}
